import React, { useEffect, useMemo, useState } from 'react';
import { createContext } from 'react';
import { getProductCategoriesData, getStoreContentData, getDriverScreeningConfig } from '../../../api/Product';
import FullScreenOverlayLoader from '../../../components/FullScreenOverlayLoader';
import { useCmsEdit } from '../../../cms/CmsEditProvider';

export interface StoreDetailProps {
  id: number;
  brand_id: number;
  store_name: string;
  language_id: string;
  logo_url: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
  phone_number: string;
  sales_tax: number | null;
  store_wavier: any;
  disclaimer: string;
  document_id: number | null;
  is_document: boolean | null;
  pickup_time: string;
  dropoff_time: string;
  cut_off_time: string;
  valid_start_date: string;
  process_mode: number;
  blockDates: Array<any>;
  is_license_required: boolean;
  is_insurance_required: boolean;
  is_agreement_required: boolean;
  is_rental: boolean;
  is_bodhi_protect_enabled: boolean;
  bodhi_protect_default: boolean;
  bodhi_protect_percentage: string;
  store_belong_to: string;
  driving_questions_config?: any;
  is_service_fee_enabled: boolean;
  service_fee_percentage: string;
  brands: {
    is_partner_site?: boolean;
    offer_pickup?: boolean | number;
  };
  isQuickOrder: boolean;
  isRentalFlow: boolean;
  bac_limit: string;
  stripe_public_key?: string;
  allow_partial_day_rentals: boolean;
  require_usps_address: boolean;
  usps_zip_codes?: string;
}

export type StoreContentItem = Record<string, string>;

interface ContextProps {
  storeDetails: StoreDetailProps;
  setStoreDetails: (storeDetails: StoreDetailProps) => void;
  getStoreDetails: () => StoreDetailProps;
  setStoreValue: (key: keyof StoreDetailProps, value: any) => void;
  getStoreValue: (key: keyof StoreDetailProps) => any | null;

  discounts: any[];
  setDiscounts: (discounts: any[]) => void;

  priceLogicData: any[];
  setPriceLogicData: (data: any[]) => void;

  storeContent: StoreContentItem;
  setStoreContent: (content: StoreContentItem) => void;

  categories: any[];
  fetchCategories: () => void;

  showLoader: () => void;
  hideLoader: () => void;
  isLoading: boolean;

  hasAutoProceeded: boolean;
  setHasAutoProceeded: (value: boolean) => void;
}

const initializedDetail: StoreDetailProps = {
  id: 0,
  brand_id: 0,
  store_name: "",
  language_id: "",
  logo_url: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  latitude: null,
  longitude: null,
  phone_number: "",
  sales_tax: null,
  store_wavier: '',
  disclaimer: '',
  document_id: null,
  is_document: null,
  pickup_time: '8:00 AM',
  dropoff_time: '10:00 AM',
  cut_off_time: '11:59 PM',
  valid_start_date: '',
  process_mode: 0,
  blockDates: [],
  is_license_required: false,
  is_insurance_required: false,
  is_agreement_required: false,
  is_rental: false,
  is_bodhi_protect_enabled: false,
  bodhi_protect_default: true,
  bodhi_protect_percentage: "0.00",
  store_belong_to: "",
  driving_questions_config: null,
  is_service_fee_enabled: false,
  service_fee_percentage: "0.00",
  brands: {},
  isQuickOrder: false,
  isRentalFlow: false,
  bac_limit: "0.00",
  stripe_public_key: undefined,
  allow_partial_day_rentals: false,
  require_usps_address: false,
  usps_zip_codes: undefined,
}

export const StoreDetailsContext = createContext<ContextProps>({
  storeDetails: initializedDetail,
  setStoreDetails: () => { },
  getStoreDetails: () => initializedDetail,
  setStoreValue: () => { },
  getStoreValue: () => null,
  discounts: [],
  setDiscounts: () => { },
  priceLogicData: [],
  setPriceLogicData: () => { },
  storeContent: {},
  setStoreContent: () => { },
  categories: [],
  fetchCategories: () => { },
  showLoader: () => { },
  hideLoader: () => { },
  isLoading: false,
  hasAutoProceeded: false,
  setHasAutoProceeded: () => {},
});

// Coerce truthy API representations (true, 1, "1", "true") to a real boolean.
// The API may return these flags as numbers or strings; treating them loosely
// can cause required document/upload steps to be silently skipped.
const toBool = (value: any): boolean =>
  value === true || value === 1 || value === "1" ||
  (typeof value === "string" && value.trim().toLowerCase() === "true");

const normalizeStoreDetails = (details: StoreDetailProps): StoreDetailProps => ({
  ...details,
  is_license_required: toBool(details?.is_license_required),
  is_insurance_required: toBool(details?.is_insurance_required),
  is_agreement_required: toBool(details?.is_agreement_required),
  require_usps_address: toBool(details?.require_usps_address),
});

export const StoreDetailsProvider = ({ children }: { children: React.ReactNode }) => {
  const [storeDetails, setStoreDetailsRaw] = useState<StoreDetailProps>(initializedDetail);

  const setStoreDetails = (details: StoreDetailProps) => {
    setStoreDetailsRaw(normalizeStoreDetails(details));
  };
  const [discounts, setDiscounts] = useState<any[]>([]);
  const [priceLogicData, setPriceLogicData] = useState<Array<any>>([]);
  const [savedStoreContent, setStoreContent] = useState<StoreContentItem>({});
  const [categories, setCategories] = useState<any[]>([]);

  // CMS Edit Mode: admin-pushed draft values overlay the saved content so the
  // whole page previews an edit live. Empty (no-op) for customers.
  const { draft: cmsDraft } = useCmsEdit();
  const storeContent = useMemo<StoreContentItem>(
    () => (Object.keys(cmsDraft).length ? { ...savedStoreContent, ...cmsDraft } : savedStoreContent),
    [savedStoreContent, cmsDraft],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasAutoProceeded, setHasAutoProceeded] = useState(false);

  const showLoader = () => setIsLoading(true);
  const hideLoader = () => setIsLoading(false);

  /** Fetch Categories Based on Brand */
  const fetchCategories = () => {
    if (!storeDetails.brand_id) return;

    getProductCategoriesData((jsonRes: any, status: any) => {
      if (status === 200 && Array.isArray(jsonRes)) {
        const filtered = jsonRes.filter((item: any) => {
          if (!item.brand_ids) return false;
          const brandIds = JSON.parse(item.brand_ids);
          return brandIds.includes(storeDetails.brand_id);
        });

        setCategories(filtered);
      } else {
        setCategories([]);
      }
    });
  };

  // /** Auto-fetch categories based on brand_id */
  // useEffect(() => {
  //   fetchCategories();
  // }, [storeDetails.brand_id]);

  /** Fetch Store Content */
  useEffect(() => {
    if (storeDetails.brand_id) {
      getStoreContentData(storeDetails.brand_id, (jsonRes: any, status) => {
        if (status === 200) {
          setStoreContent(jsonRes);
        } else {
          setStoreContent({});
        }
      });

      // Fetch driving questions config
      getDriverScreeningConfig({ brand_id: storeDetails.brand_id }, (jsonRes: any, status) => {
        if (status === 200 && jsonRes?.questions) {
          const data = typeof jsonRes.questions === 'string' ? JSON.parse(jsonRes.questions) : jsonRes.questions || {};
          setStoreValue('driving_questions_config', data);
        } else {
          setStoreValue('driving_questions_config', null);
        }
      });
    }
  }, [storeDetails.brand_id]);

  const getStoreDetails = () => {
    return storeDetails;
  };

  const setStoreValue = (key: keyof StoreDetailProps, value: string | number | null) => {
    setStoreDetailsRaw((prevDetails) => {
      const base = prevDetails ?? initializedDetail;
      return normalizeStoreDetails({ ...base, [key]: value });
    });
  };

  const getStoreValue = (key: keyof StoreDetailProps): any | null => {
    return storeDetails ? storeDetails[key] : null;
  };

  const values: ContextProps = {
    storeDetails,
    setStoreDetails,
    getStoreDetails,
    setStoreValue,
    getStoreValue,

    discounts,
    setDiscounts,

    priceLogicData,
    setPriceLogicData,

    storeContent,
    setStoreContent,

    categories,
    fetchCategories,

    showLoader,
    hideLoader,
    isLoading,

    hasAutoProceeded,
    setHasAutoProceeded,
  };

  return (
    <StoreDetailsContext.Provider value={values}>
      {children}
      <FullScreenOverlayLoader isVisible={isLoading} />
    </StoreDetailsContext.Provider>
  );
};